import { useState, useEffect } from 'react'
import { Navigate, useParams, useSearchParams, useNavigate } from 'react-router'
import { RotateCcw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { CallStagingHeader } from '../components/call-staging/CallStagingHeader'
import { CallStatusPanel } from '../components/call-staging/CallStatusPanel'
import { CallStagingBriefing } from '../components/contact/CallStagingBriefing'
import { PhoneCallHistory } from '../components/phone/PhoneCallHistory'
import { useActiveCallForPhone } from '../components/phone/useActiveCallForPhone'
import { useContactByPhone } from '../api/contacts'

export function AgentCallPage() {
  const { t } = useTranslation('calls')
  const { phoneNumber: rawParam } = useParams()
  const phoneNumber = rawParam ? decodeURIComponent(rawParam) : ''
  const [searchParams] = useSearchParams()
  const contactId = searchParams.get('contactId')
  const navigate = useNavigate()

  const activeCall = useActiveCallForPhone(phoneNumber)
  const [lastCallSid, setLastCallSid] = useState<string | undefined>()

  const explicitContactId = contactId ? Number(contactId) : undefined
  const { data: phoneContact } = useContactByPhone(
    explicitContactId ? undefined : phoneNumber,
  )
  const briefingContactId = explicitContactId ?? phoneContact?.id

  useEffect(() => {
    // FOLLOW-UP(react-compiler): local state synced from the SSE telemetry store.
    // It has multiple writers (this effect plus user event handlers), so deriving
    // it during render is a state-machine redesign, not a mechanical fix — and the
    // UI has no JS test suite to catch a regression. Suppressed, not fixed.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLastCallSid(undefined)
  }, [phoneNumber])

  useEffect(() => {
    if (activeCall) {
      // FOLLOW-UP(react-compiler): local state synced from the SSE telemetry store.
      // It has multiple writers (this effect plus user event handlers), so deriving
      // it during render is a state-machine redesign, not a mechanical fix — and the
      // UI has no JS test suite to catch a regression. Suppressed, not fixed.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLastCallSid(activeCall.call_sid)
    }
  }, [activeCall])

  const callEnded = !activeCall && !!lastCallSid

  if (!phoneNumber) {
    return <Navigate to="/app/calls/stage/" replace />
  }

  return (
    <div className="space-y-6">
      <CallStagingHeader
        phoneNumber={phoneNumber}
        contactId={contactId ? Number(contactId) : undefined}
      />

      {callEnded && (
        <div className="flex items-center justify-between rounded-lg border border-border bg-surface-card p-4">
          <p className="text-sm text-text-secondary">
            {t('staging.callCompleted')}
          </p>
          <button
            onClick={() => navigate('/app/calls/stage/')}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
          >
            <RotateCcw className="h-4 w-4" />
            {t('staging.stageNewCall')}
          </button>
        </div>
      )}

      {briefingContactId != null && (
        <CallStagingBriefing contactId={briefingContactId} />
      )}

      <CallStatusPanel phoneNumber={phoneNumber} />

      <PhoneCallHistory phoneNumber={phoneNumber} />
    </div>
  )
}
